import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { learnerStatusApi, type LeaveHistoryItem } from '@/api/learnerStatus'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@quebon/ui/components/dialog'

const STATUS_BADGE: Record<LeaveHistoryItem['status'], string> = {
  active: 'bg-amber-100 text-amber-700',
  ended: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-50 text-red-500 line-through',
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

function canCancelLeave(item: LeaveHistoryItem, today: string): boolean {
  return item.status !== 'cancelled' && item.leaveStart > today
}

function canEditLeave(item: LeaveHistoryItem, today: string): boolean {
  if (item.status === 'cancelled') return false
  return item.leaveStart <= today && (!item.leaveEnd || today <= item.leaveEnd)
}

/**
 * 원생 휴원 이력 목록/수정/개별취소 (P5 축4=A). 원생 상세/수정 모달에서 사용.
 * enrollment 0건(404)/로딩/에러는 섹션 숨김(fail-soft). 진행중(active) 취소=철회(재원 복귀).
 */
export function LeaveHistorySection({ userId }: { userId: number }) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<LeaveHistoryItem | null>(null)

  const { data, isError, isLoading } = useQuery({
    queryKey: ['lc', 'leaveHistory', userId],
    queryFn: async () => (await learnerStatusApi.getLeaveHistory(userId)).data.data,
    retry: false,
  })

  const apiErrorMessage = (err: unknown, fallback: string): string => {
    const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code
    return code ? t(`apiError.${code}`, { ns: 'common', defaultValue: fallback }) : fallback
  }

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lc', 'leaveHistory', userId] })
    queryClient.invalidateQueries({ queryKey: ['lc', 'learnerStatusSummary', userId] })
    queryClient.invalidateQueries({ queryKey: ['lc', 'learnerStatuses'] })
  }

  const cancelMutation = useMutation({
    mutationFn: (historyId: number) => learnerStatusApi.cancelLeaveHistory(userId, historyId),
    onSuccess: () => { invalidate(); toast.success(t('students.leaveHistory.toast.cancelSuccess')) },
    onError: (err) => toast.error(apiErrorMessage(err, t('students.leaveHistory.toast.cancelFailed'))),
  })

  if (isLoading || isError || !data || data.length === 0) return null

  const statusLabel = (s: LeaveHistoryItem['status']): string => t(`students.leaveHistory.status.${s}`)
  const today = todayIsoDate()

  return (
    <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 space-y-2">
      <span className="text-sm font-medium text-gray-700">{t('students.leaveHistory.title')}</span>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500">
              <th className="py-2 pr-3">{t('students.leaveHistory.table.no')}</th>
              <th className="py-2 pr-3">{t('students.leaveHistory.table.start')}</th>
              <th className="py-2 pr-3">{t('students.leaveHistory.table.end')}</th>
              <th className="py-2 pr-3">{t('students.leaveHistory.table.reason')}</th>
              <th className="py-2 pr-3">{t('students.leaveHistory.table.status')}</th>
              <th className="py-2 text-right">{t('students.leaveHistory.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((h, index) => {
              const editEnabled = canEditLeave(h, today)
              const cancelEnabled = canCancelLeave(h, today)
              return (
                <tr key={h.id} data-testid={`leave-history-${h.id}`} className="text-gray-700">
                  <td className="py-2 pr-3">{index + 1}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{h.leaveStart}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{h.leaveEnd ?? '-'}</td>
                  <td className="py-2 pr-3">{h.reason || '-'}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[h.status]}`}>
                      {statusLabel(h.status)}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        data-testid={`leave-history-edit-${h.id}`}
                        disabled={!editEnabled}
                        onClick={() => setEditing(h)}
                        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        {t('students.leaveHistory.edit')}
                      </button>
                      <button
                        type="button"
                        data-testid={`leave-history-cancel-${h.id}`}
                        disabled={!cancelEnabled || cancelMutation.isPending}
                        onClick={() => {
                          if (window.confirm(t('students.leaveHistory.confirmCancel'))) cancelMutation.mutate(h.id)
                        }}
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        {t('students.leaveHistory.cancel')}
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <LeaveHistoryEditModal
          userId={userId}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  )
}

interface EditProps {
  userId: number
  item: LeaveHistoryItem
  onClose: () => void
  onSaved: () => void
}

function LeaveHistoryEditModal({ userId, item, onClose, onSaved }: EditProps) {
  const { t } = useTranslation(['admin', 'common'])
  const [leaveStart, setLeaveStart] = useState(item.leaveStart)
  const [leaveEnd, setLeaveEnd] = useState(item.leaveEnd ?? '')
  const [reason, setReason] = useState(item.reason ?? '')

  const apiErrorMessage = (err: unknown, fallback: string): string => {
    const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code
    return code ? t(`apiError.${code}`, { ns: 'common', defaultValue: fallback }) : fallback
  }

  const mutation = useMutation({
    mutationFn: () =>
      learnerStatusApi.updateLeaveHistory(userId, item.id, {
        leaveStart,
        leaveEnd: leaveEnd || undefined,
        reason: reason || undefined,
      }),
    onSuccess: () => { onSaved(); toast.success(t('students.leaveHistory.toast.editSuccess')); onClose() },
    onError: (err) => toast.error(apiErrorMessage(err, t('students.leaveHistory.toast.editFailed'))),
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('students.leaveHistory.editTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('students.leaveHistory.field.start')}</label>
              <input
                type="date"
                data-testid="leave-history-edit-start"
                value={leaveStart}
                onChange={(e) => setLeaveStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('students.leaveHistory.field.end')}</label>
              <input
                type="date"
                data-testid="leave-history-edit-end"
                value={leaveEnd}
                onChange={(e) => setLeaveEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('students.leaveHistory.field.reason')}</label>
            <input
              type="text"
              data-testid="leave-history-edit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            {t('button.cancel', { ns: 'common' })}
          </button>
          <button
            type="button"
            data-testid="leave-history-edit-submit"
            disabled={mutation.isPending || !leaveStart}
            onClick={() => mutation.mutate()}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {t('button.save', { ns: 'common' })}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
